import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { EMPTY } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ChevronRightIconComponent } from '@kuiper/icons';
import { AvatarComponent } from '../../../../ui-components/lib/avatar/avatar.component';
import { ClickOutSideDirective } from '../../../../ui-components/directives/clickOutSide.directive';
import { EMPLOYEE_ROUTES } from '../../constants/employee.routing.constants';
import { Language } from '../../models/language.model';
import { AuthService } from '../../services/auth/auth.service';
import { MainService } from '../../services/main/main.service';
import {
  Portal,
  ProfileActionsService,
} from '../profile-dropdown/services/profile-actions.service';

const PORTAL_LABELS: Record<Portal, string> = {
  employee: 'profile_menu_employee_app_label',
  admin: 'profile_menu_admin_dashboard_label',
  vendor: 'profile_menu_vendor_portal_label',
};
const GO_TO_LABELS: Record<Portal, string> = {
  employee: 'sidenav_go_to_employees_dashboard_item_label',
  admin: 'sidenav_go_to_admin_dashboard_item_label',
  vendor: 'profile_menu_go_to_vendor_portal_label',
};

@Component({
  selector: 'lib-side-menu-profile',
  imports: [
    RouterLink,
    TranslocoPipe,
    AvatarComponent,
    ClickOutSideDirective,
    ChevronRightIconComponent,
  ],
  templateUrl: './side-menu-profile.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:keydown.escape)': 'close()' },
})
export class SideMenuProfileComponent {
  private readonly authService = inject(AuthService);
  private readonly mainService = inject(MainService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly actions = inject(ProfileActionsService);

  readonly collapsed = input<boolean>(false);

  protected readonly editProfilePath =
    '/' + EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.EDIT_PROFILE;
  protected readonly accountSettingsPath =
    '/' + EMPLOYEE_ROUTES.ACCOUNT_SETTINGS.PROFILE_DETAILS;
  protected readonly portalLabels = PORTAL_LABELS;
  protected readonly languages: Language[] = ['en', 'ar'];

  protected readonly isOpen = computed(
    () => this.mainService.openOverlay() === 'profile'
  );
  protected readonly popup = signal<'workspaces' | 'portal' | null>(null);
  protected readonly lang = signal<Language>(
    this.translocoService.getActiveLang() as Language
  );

  protected readonly userName = computed(() => {
    const central = this.authService.centralUser();
    return (
      this.authService.user()?.name ??
      `${central?.first_name ?? ''} ${central?.last_name ?? ''}`.trim()
    );
  });
  protected readonly userImage = computed(
    () => this.authService.centralUser()?.image?.url ?? ''
  );
  protected readonly workspace = computed(() =>
    this.authService.selectedWorkspace()
  );
  protected readonly workspaceName = computed(
    () => this.workspace()?.name ?? this.workspace()?.slug ?? ''
  );
  protected readonly workspaces = computed(() =>
    (this.authService.workspaces() ?? []).map(ws => ({
      slug: ws.slug,
      name: ws.name || ws.slug,
      line:
        ws.business_line?.name?.[this.lang()] || ws.business_line?.slug || '',
      current: ws.slug === this.workspace()?.slug,
    }))
  );
  /** Picks the target of the single link: 2 reachable → link, 3 → Switch portal pop-up, ≤1 → nothing. */
  protected readonly otherPortals = computed(() =>
    this.actions
      .reachablePortals()
      .filter(p => p !== this.actions.currentPortal())
  );
  protected readonly goToLabel = computed(
    () => GO_TO_LABELS[this.otherPortals()[0] ?? 'employee']
  );

  toggle(): void {
    this.popup.set(null);
    this.mainService.openOverlay.set(this.isOpen() ? null : 'profile');
  }

  close(): void {
    if (this.isOpen()) {
      this.popup.set(null);
      this.mainService.openOverlay.set(null);
    }
  }

  openWorkspaces(): void {
    if (this.popup() === 'workspaces') {
      this.popup.set(null);
      return;
    }
    this.popup.set('workspaces');
    // a failed refresh keeps the last known list
    this.actions
      .loadWorkspaces()
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  openPortals(): void {
    this.popup.set(this.popup() === 'portal' ? null : 'portal');
  }

  switchWorkspace(slug: string): void {
    this.close();
    this.actions.switchWorkspace(slug);
  }

  createWorkspace(): void {
    this.close();
    this.actions.createWorkspace();
  }

  switchPortal(target: Portal): void {
    this.close();
    this.actions.switchPortal(target);
  }

  changeLanguage(lang: Language): void {
    this.lang.set(lang);
    this.actions.changeLanguage(lang);
  }

  signOut(): void {
    this.close();
    this.actions.logout();
  }
}
